import { useState } from 'react';

export type CustomerInfoType = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export type NodeSettings = {
  requirePhone: boolean;
  requireEmail: boolean;
  showName: boolean;
  showLabels: boolean;
  showValidation: boolean;
  displayStyle: string;
  colors: ThemeColors;
  showTerms?: boolean;
  [key: string]: any; // Add index signature to accept any additional properties
}

export type ThemeColors = {
  cardBackground: string;
  cardBorder: string;
  cardBorderSelected: string;
  cardShadow: string;
  cardShadowSelected: string;
  titleText: string;
  subtitleText: string;
  inputBackground: string;
  inputBorder: string;
  inputText: string;
  inputPlaceholder: string;
  iconColor: string;
  validationIconColor: string;
  dividerColor: string;
  requiredFieldMark: string;
  selectionBackground: string;
  selectionIcon: string;
  badgeBackground: string;
  badgeBorder: string;
  infoText: string;
  buttonBackground: string;
  buttonText: string;
  placeholderGradientFrom: string;
  placeholderGradientTo: string;
  placeholderIconBackground: string;
  [key: string]: string; // Add index signature to accept any string keys
}

type ErrorState = {
  [K in keyof CustomerInfoType]: string;
};

type TouchedState = {
  [K in keyof CustomerInfoType]: boolean;
};

export const hexToRgba = (hex: string) => {
  // Handle hex with alpha (#RRGGBBAA)
  if (hex.length === 9) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const a = parseInt(hex.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  // Handle standard hex (#RRGGBB)
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, 1)`;
};

export const useCustomerInfo = (initialState: CustomerInfoType) => {
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoType>(initialState);
  const [errors, setErrors] = useState<ErrorState>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [touched, setTouched] = useState<TouchedState>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
  });

  const validateName = (name: string, fieldName: string): string => {
    if (!name.trim()) {
      return `${fieldName} is required`;
    }
    if (name.trim().length < 2) {
      return `${fieldName} must be at least 2 characters long`;
    }
    if (name.trim().length > 50) {
      return `${fieldName} must not exceed 50 characters`;
    }
    const nameRegex = /^[a-zA-Z\s\-']+$/;
    if (!nameRegex.test(name)) {
      return `${fieldName} can only contain letters, spaces, hyphens, and apostrophes`;
    }
    if (/\s{2,}|-{2,}|'{2,}/.test(name)) {
      return `${fieldName} cannot contain consecutive spaces, hyphens, or apostrophes`;
    }
    if (/^[\s\-']|[\s\-']$/.test(name)) {
      return `${fieldName} cannot start or end with a space, hyphen, or apostrophe`;
    }
    return '';
  };

  const validateField = (name: keyof CustomerInfoType, value: string): string => {
    switch (name) {
      case 'firstName':
        return validateName(value, 'First name');
      case 'lastName':
        return validateName(value, 'Last name');
      case 'email':
        if (!value.trim()) {
          return 'Email is required';
        }
        const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return emailRegex.test(value) ? '' : 'Invalid email address';
      case 'phone':
        if (!value.trim()) {
          return 'Phone number is required';
        }
        return value.replace(/\D/g, '').length >= 10 ? '' : 'Invalid phone number';
      default:
        return '';
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name as keyof CustomerInfoType, value) }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handlePhoneChange = (value: string) => {
    setCustomerInfo((prev) => ({ ...prev, phone: value }));
    setErrors((prev) => ({ ...prev, phone: validateField('phone', value) }));
    setTouched((prev) => ({ ...prev, phone: true }));
  };

  const handleBlur = (fieldName: keyof CustomerInfoType) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    // Validate the field when it loses focus
    const value = customerInfo[fieldName];
    setErrors((prev) => ({ ...prev, [fieldName]: validateField(fieldName, value) }));
  };

  const isValid = Object.values(errors).every(error => !error) &&
                  Object.values(customerInfo).every(value => value.trim() !== '');

  return {
    customerInfo,
    setCustomerInfo,
    errors,
    touched,
    handleChange,
    handlePhoneChange,
    handleBlur,
    isValid
  };
};
